/**
 * 파일명: sectionReveal.js
 * 작성자: 김준형
 * 작성일자: 26.08.10
 * 설명: Landing - 섹션 진입 연출 래퍼.
 */
import React, { createContext, useEffect, useRef, useState } from "react";
import style from "css/landing.module.css";

const SHOW_MARGIN = "0px 0px -15% 0px";
const HIDE_MARGIN = "0px";

const TRANSPARENT = ["rgba(0, 0, 0, 0)", "transparent", ""];

/**
 * 진입 연출의 이동 시간
 */
export const REVEAL_DURATION_MS = 1500;

/**
 * 섹션이 나타난 상태인지를 자식에게 알림
 */
export const SectionRevealContext = createContext(true);

/**
 * 첫 화면처럼 로드 시점에 이미 보이는 섹션에 쓴다.
 */
function SectionReveal({ children, defaultOn = false }) {
  const sectionRef = useRef(null);
  const innerRef = useRef(null);
  const [isShown, setShown] = useState(defaultOn);
  const [isOnScreen, setOnScreen] = useState(defaultOn);

  /*
    래퍼에 섹션과 같은 배경색으로 지정
  */
  useEffect(() => {
    const el = sectionRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return undefined;

    const syncBackdrop = () => {
      const section = inner.firstElementChild;
      if (!section) return;

      const bg = window.getComputedStyle(section).backgroundColor;
      if (TRANSPARENT.includes(bg)) return;

      el.style.backgroundColor = bg;
    };

    syncBackdrop();
    window.addEventListener("resize", syncBackdrop);

    return () => window.removeEventListener("resize", syncBackdrop);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;

    // 미지원 환경에서는 내용을 바로 표시
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      setOnScreen(true);
      return undefined;
    }

    const showObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        setShown(true);
        showObserver.disconnect();
      },
      { threshold: 0, rootMargin: SHOW_MARGIN },
    );

    const screenObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setOnScreen(entry.isIntersecting));
      },
      { threshold: 0, rootMargin: HIDE_MARGIN },
    );

    showObserver.observe(el);
    screenObserver.observe(el);

    return () => {
      showObserver.disconnect();
      screenObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={sectionRef}
      className={style.section_reveal}
    >
      <div
        ref={innerRef}
        className={`${style.section_reveal_inner} ${
          isShown ? style.section_reveal_done : style.section_reveal_pre
        }`}
      >
        <SectionRevealContext.Provider value={isShown && isOnScreen}>
          {children}
        </SectionRevealContext.Provider>
      </div>
    </div>
  );
}

export default SectionReveal;
